import type {ChatModel} from './chatModel';

export const STYLE_GUIDE_PARAMS = {
  assetName: 'Style guide',
  slug: 'style-guide',
};

/**
 * The page-count options every reader of an AISB site's static page count must
 * pass, so the publish paywall, the `Project Published` property and the
 * overage census all count the same pages.
 *
 * The generated Style guide is exempt: the AISB page picker hides it and the
 * creation paywall already skips it, so counting it pushes otherwise-compliant
 * sites over their entitlement (LIF-467).
 */
export const AISB_STATIC_PAGE_COUNT_OPTS = {
  excludeSlugs: [STYLE_GUIDE_PARAMS.slug],
} as const;

export const AI_BRIDGE_SCOPES = ['aila', 'agent-service'] as const;

export const INSTALLED_WF_LIBRARY_NAME = 'Design System';

export const AI_WORKSPACE_SETTINGS_FIELDS = [
  'aiWriteEnabled',
  'copilotEnabled',
];

export const AI_SITE_SETTINGS_FIELDS = ['aiWriteEnabled', 'copilotEnabled'];

// Used as quick prompts for the AI Writing Assistant on bound and non-bound text elements
export const REWRITE = 'REWRITE' as const;
export const MAKE_SHORTER = 'MAKE_SHORTER' as const;
export const MAKE_LONGER = 'MAKE_LONGER' as const;
export const MAKE_MORE_PROFESSIONAL = 'MAKE_MORE_PROFESSIONAL' as const;
export const MAKE_MORE_CASUAL = 'MAKE_MORE_CASUAL' as const;
export const MAKE_MORE_FRIENDLY = 'MAKE_MORE_FRIENDLY' as const;
export const FIX_SPELLING_AND_GRAMMAR = 'FIX_SPELLING_AND_GRAMMAR' as const;

export const AI_WRITE_QUICK_MOD_PROMPTS = {
  [REWRITE]: 'Rewrite',
  [MAKE_SHORTER]: 'Make shorter',
  [MAKE_LONGER]: 'Make longer',
  [MAKE_MORE_PROFESSIONAL]: 'Make more professional',
  [MAKE_MORE_CASUAL]: 'Make more casual',
  [MAKE_MORE_FRIENDLY]: 'Make more friendly',
  [FIX_SPELLING_AND_GRAMMAR]: 'Fix spelling and grammar',
} as const;

export const COMPONENT_CATEGORIES = [
  '404',
  'banner',
  'blog body',
  'career',
  'coming soon',
  'contact',
  'content feed',
  'content header',
  'cta',
  'event feed',
  'event header',
  'faq',
  'feature',
  'footer',
  'gallery',
  'hero',
  'logo',
  'marquee',
  'metrics',
  'nav',
  'pricing',
  'team',
  'testimonial',
] as const;

export const UNSUPPORTED_FILE_TYPES = ['SVG'];
export const SUPPORTED_FILE_TYPES = ['JPG', 'PNG', 'GIF', 'WEBP'];

// AI Assistant Model Configuration
export const AI_ASSISTANT_MODEL_CONFIG_ID = 'aia-default-model';

export const AIA_TEMPERATURE = 0.0;

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high';

export type AssistantModelConfig = {
  defaultModel: ChatModel;
  reasoningEffort?: ReasoningEffort;
  temperature?: number;
} & Record<string, unknown>;

export const AI_ASSISTANT_MODEL_CONFIG_DEFAULT: AssistantModelConfig = {
  defaultModel: {
    provider: 'google',
    name: 'gemini-3.8-flash',
  },
  reasoningEffort: 'medium',
  temperature: AIA_TEMPERATURE,
};

export const DEFAULT_TOOL_RESULT_COMPACTION_MODEL = {
  provider: 'google',
  name: 'gemini-3.5-flash',
} as const;

export const COMBINED_AIA_FILESYSTEM_DEFAULT_MODEL = {
  provider: 'openai',
  name: 'gpt-5.6-luna',
} as const;

export const VCC_AI_ASSISTANT_MODEL_CONFIG_ID = 'vcc-aia-default-model';

export const VCC_AI_ASSISTANT_MODEL_CONFIG_DEFAULT: AssistantModelConfig = {
  defaultModel: {
    provider: 'anthropic',
    name: 'claude-opus-5',
  },
  reasoningEffort: 'low',
};

export type AiaSummarizationConfig = {
  contextWindowRatios: {
    aia: number;
    vcc: number;
  };
};

export const AIA_SUMMARIZATION_CONFIG_ID = 'dc-apai-aia-summarization-config';

export const AIA_SUMMARIZATION_CONFIG_DEFAULT: AiaSummarizationConfig = {
  contextWindowRatios: {
    aia: 0.65,
    vcc: 0.8,
  },
};

export const AICC_VISUAL_CHANGES_MODEL_CONFIG_ID = 'aicc-visual-changes-model';

// AICC visual-changes turns need a cheap model that still supports reasoning;
// non-reasoning models produced incorrect edits during manual testing.
export const AICC_VISUAL_CHANGES_MODEL_CONFIG_DEFAULT: AssistantModelConfig = {
  defaultModel: {
    provider: 'google',
    name: 'gemini-3.1-flash-lite',
  },
  reasoningEffort: 'low',
};

export const AICC_CLARIFICATION_MODEL_CONFIG_ID = 'aicc-clarification-model';

// AICC clarification turns only need to emit ask-user-question — use a fast
// Flash model so the fullscreen spinner stays short. Generation after the
// user answers must clear this override (see prompt-generator HITL resume).
export const AICC_CLARIFICATION_MODEL_CONFIG_DEFAULT: AssistantModelConfig = {
  defaultModel: {
    provider: 'google',
    name: 'gemini-3.5-flash',
  },
  reasoningEffort: 'none',
};

export type FallbackModelsConfig = {
  fallbackModels: ChatModel[];
} & Record<string, unknown>;

export const VCC_ANTHROPIC_FALLBACK_CONFIG_ID = 'vcc-anthropic-fallback-models';

export const VCC_ANTHROPIC_FALLBACK_CONFIG_DEFAULT: FallbackModelsConfig = {
  fallbackModels: [{provider: 'openai', name: 'gpt-5.5'}],
};
